

























































































import type { ComputedRef, PropType, Ref } from '@vue/composition-api';
import { IconSize } from '@getaccept/lib-ui';
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  ref,
  nextTick,
  watch,
} from '@vue/composition-api';
import type { EmojiIndex } from 'emoji-mart-vue-fast';
import { store } from 'emoji-mart-vue-fast';
import type { Emoji } from '@getaccept/lib-ui';
import { EmojiHelper } from '@getaccept/lib-shared/src/emoji/helpers/emoji.helper';
import { EMOJI_INLINE_TRIGGER_COUNT } from '@getaccept/lib-shared/src/emoji/constants/emoji.constants';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import { ChatHelper } from '../helpers/chat.helper';
import { ChatConstants } from '../constants/chat-constants';
import type { Message } from '../types';

export default defineComponent({
  name: 'ChatActions',
  props: {
    replyMessage: {
      type: Object as PropType<Message>,
      default: undefined,
    },
    user: {
      type: Object as PropType<User>,
      default: undefined,
    },
    recipients: {
      type: Array as PropType<Array<Recipient>>,
      default: () => [],
    },
  },
  emits: ['send', 'typing', 'clear-reply'],
  setup(props, { emit }) {
    const inputRef: Ref<HTMLTextAreaElement> = ref(null);
    const { replyMessage, recipients, user } = toRefs(props);
    const emojiIndex = ref<EmojiIndex>(null);

    const localState = reactive({
      text: '',
      skinTone: 0,
      emojiSearch: '',
      selectedInlineEmojiIndex: 0,
      showSendButtonTooltip: false,
      showEmojiButtonTooltip: false,
      showEmojis: false,
      showInlineEmojiMenu: false,
      isTyping: false,
      localization: ChatHelper.getEmojiLocalization(),
    });

    onMounted(() => {
      localState.skinTone = store.get('skin');
    });

    const matchedEmojis: ComputedRef<Emoji[]> = computed(() =>
      EmojiHelper.getEmojiListByKey(localState.emojiSearch, emojiIndex.value)
    );

    const correctEmojiSearch: ComputedRef<boolean> = computed(
      () =>
        localState.emojiSearch.charAt(0) === ':' &&
        localState.emojiSearch.length >= EMOJI_INLINE_TRIGGER_COUNT &&
        matchedEmojis.value.length > 0 &&
        !localState.emojiSearch.includes(' ')
    );

    const handleSkinChange = (skinTone: number): void => {
      localState.skinTone = skinTone;
    };

    const getEmojiKey = (element: HTMLTextAreaElement): string => {
      const end: number = element.selectionEnd;
      const start: number = localState.text.lastIndexOf(':', end - 2);
      return start >= 0 ? localState.text.substring(start, end) : '';
    };

    const setShowInlineEmojiMenu = (show: boolean): void => {
      localState.showInlineEmojiMenu = show;
    };

    const resetInlineEmojiMenu = (): void => {
      setShowInlineEmojiMenu(false);
      localState.emojiSearch = '';
      localState.selectedInlineEmojiIndex = 0;
    };

    const replaceEmojiSearchWithEmoji = async (emoji: Emoji): Promise<void> => {
      const nativeEmoji: string = EmojiHelper.getEmojiWithSkinTone(emoji, localState.skinTone)
        ?.native;
      if (!nativeEmoji) {
        resetInlineEmojiMenu();
        return;
      }
      const cursorPosition: number =
        inputRef.value.selectionEnd - localState.emojiSearch.length + nativeEmoji.length;
      localState.text = localState.text.replace(localState.emojiSearch, nativeEmoji);
      resetInlineEmojiMenu();
      await nextTick();
      inputRef.value.setSelectionRange(cursorPosition, cursorPosition);
    };

    const setShowEmojiPicker = (show: boolean): void => {
      localState.showEmojis = show;
    };

    const handleAddEmoji = async (emoji: Emoji): Promise<void> => {
      const cursorPosition: number = inputRef.value.selectionEnd;
      setShowEmojiPicker(false);
      localState.text =
        localState.text.slice(0, cursorPosition) +
        emoji.native +
        localState.text.slice(cursorPosition);
      inputRef.value.focus();
      await nextTick();
      inputRef.value.setSelectionRange(
        cursorPosition + emoji.native.length,
        cursorPosition + emoji.native.length
      );
    };

    const handleSend = (): void => {
      inputRef.value.focus();
      if (localState.text) {
        emit('send', localState.text);
        localState.text = '';
      }
    };

    const clickOutside = (): void => {
      if (!localState.showEmojis) {
        return;
      }
      setShowEmojiPicker(false);
    };

    const handleEnter = (): void => {
      if (localState.showInlineEmojiMenu) {
        replaceEmojiSearchWithEmoji(matchedEmojis.value[localState.selectedInlineEmojiIndex]);
      } else {
        handleSend();
        handleTypingStatus(false);
      }
    };

    const handleTab = (event: KeyboardEvent): void => {
      if (localState.showInlineEmojiMenu) {
        event.preventDefault();
        replaceEmojiSearchWithEmoji(matchedEmojis.value[localState.selectedInlineEmojiIndex]);
      }
    };

    const handleNavigate = (event: KeyboardEvent): void => {
      if (!localState.showInlineEmojiMenu) {
        return;
      }
      event.preventDefault();
      const firstSelected: boolean = localState.selectedInlineEmojiIndex === 0;
      const lastSelected: boolean =
        localState.selectedInlineEmojiIndex === matchedEmojis.value.length - 1;

      let step: number;
      switch (event.key) {
        case 'ArrowUp':
          if (firstSelected) {
            return;
          }
          step = -1;
          break;
        case 'ArrowDown':
          if (lastSelected) {
            return;
          }
          step = 1;
          break;
      }

      localState.selectedInlineEmojiIndex += step;
    };

    let resetTypingStatus: ReturnType<typeof setTimeout>;

    watch(
      () => localState.text,
      (newValue: string) => {
        if (!localState.isTyping) {
          handleTypingStatus(!!newValue);
        }

        clearTimeout(resetTypingStatus);
        resetTypingStatus = setTimeout(
          () => handleTypingStatus(false),
          ChatConstants.typingStatusTimeout
        );
      },
      { deep: true }
    );

    const handleTypingStatus = (isTyping: boolean) => {
      if (isTyping === localState.isTyping) {
        return;
      }
      localState.isTyping = isTyping;
      emit('typing', isTyping);
    };

    const handleInput = (): void => {
      localState.emojiSearch = getEmojiKey(inputRef.value);
      const isEndingColon = localState.text.charAt(localState.text.length - 1) === ':';
      localState.selectedInlineEmojiIndex = 0;

      if (isEndingColon && correctEmojiSearch.value) {
        const emoji = emojiIndex.value.findEmoji(localState.emojiSearch);
        replaceEmojiSearchWithEmoji(emoji);
        return;
      }
      if (correctEmojiSearch.value && !localState.showInlineEmojiMenu) {
        setShowInlineEmojiMenu(true);
        return;
      }
      if (!correctEmojiSearch.value) {
        resetInlineEmojiMenu();
      }
    };

    const replyHeader: ComputedRef<string> = computed(() => {
      const { recipientId } = replyMessage?.value?.owner || {};
      const recipient: Recipient =
        recipientId && recipients.value.find(({ id }: Recipient) => id === recipientId);

      return recipient?.fullName || user.value.fullName;
    });

    const handleClearReply = (): void => {
      emit('clear-reply');
    };

    watch(replyMessage, (newValue: Message) => {
      if (newValue) {
        inputRef.value.focus();
      }
    });

    onMounted(async () => {
      emojiIndex.value = await EmojiHelper.emojiIndex();
    });

    return {
      ...toRefs(localState),
      inputRef,
      handleInput,
      handleSend,
      handleEnter,
      handleTab,
      handleNavigate,
      handleSkinChange,
      handleAddEmoji,
      matchedEmojis,
      clickOutside,
      setShowEmojiPicker,
      replaceEmojiSearchWithEmoji,
      resetInlineEmojiMenu,
      replyHeader,
      handleClearReply,
      IconSize,
      emojiIndex,
    };
  },
});
